<template>
  <!--
    One item at a time, the newest, with a count of the rest. Not a carousel, and dismissing it
    dismisses them all: the banner's message is "there is news", and making someone close it once
    per item turns news into chores. Everything stays in Help > What's new.
  -->
  <div
    v-if="item"
    class="tw:mb-4 tw:md:mb-6 tw:print:hidden"
    role="region"
    aria-label="What's new"
  >
    <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-2 tw:rounded-xl tw:border tw:border-indigo-500/30 tw:bg-indigo-500/10 tw:px-4 tw:py-2.5 tw:text-sm">
      <Sparkles class="tw:w-4 tw:h-4 tw:text-indigo-400 tw:shrink-0" aria-hidden="true" />
      <span class="tw:rounded-full tw:bg-indigo-600 tw:px-2 tw:py-0.5 tw:text-2xs tw:font-semibold tw:uppercase tw:tracking-wide tw:text-white tw:shrink-0">New</span>

      <p class="tw:m-0 tw:flex-1 tw:min-w-[12rem] tw:text-slate-200">
        <span class="tw:font-semibold tw:text-slate-100">{{ item.title }}.</span>
        {{ item.summary }}
      </p>

      <div class="tw:flex tw:items-center tw:gap-2 tw:shrink-0">
        <button
          v-if="cta"
          type="button"
          class="tw:h-7 tw:px-3 tw:rounded-lg tw:bg-indigo-600 tw:text-xs tw:font-semibold tw:text-white tw:hover:bg-indigo-500 tw:transition-colors tw:cursor-pointer"
          @click="followCta"
        >
          {{ cta.label }}
        </button>
        <button
          v-else-if="upgradeCta"
          type="button"
          class="tw:h-7 tw:px-3 tw:rounded-lg tw:bg-indigo-600 tw:text-xs tw:font-semibold tw:text-white tw:hover:bg-indigo-500 tw:transition-colors tw:cursor-pointer"
          @click="upgrade"
        >
          {{ upgradeCta.text }}
        </button>
        <button
          type="button"
          class="tw:h-7 tw:px-2 tw:rounded-lg tw:text-xs tw:font-medium tw:text-indigo-400 tw:hover:bg-indigo-500/10 tw:transition-colors tw:cursor-pointer"
          @click="openPanel"
        >
          {{ moreCount > 0 ? `+${moreCount} more` : 'Learn more' }}
        </button>
        <button
          type="button"
          class="tw:w-7 tw:h-7 tw:rounded-lg tw:flex tw:items-center tw:justify-center tw:text-slate-400 tw:hover:text-slate-100 tw:hover:bg-slate-800/60 tw:transition-colors tw:cursor-pointer"
          aria-label="Dismiss"
          title="Dismiss"
          @click="dismiss"
        >
          <X class="tw:w-4 tw:h-4" />
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Sparkles, X } from '@lucide/vue'
// @ts-expect-error importing untyped JS module into TypeScript
import { logEvent } from '@/services/clients/firebase'
import { useWhatsNewStore } from '@/stores/whatsNew'
import { useWhatsNew } from './useWhatsNew'
import { followWhatsNewCta, followWhatsNewUpgrade } from './followCta'
import type { WhatsNewAction } from './whats-new-items'

const emit = defineEmits<{ action: [action: WhatsNewAction] }>()

const router = useRouter()
const whatsNewStore = useWhatsNewStore()
const { bannerItems, ctaFor, upsellFor } = useWhatsNew()

const item = computed(() => bannerItems.value[0] ?? null)
const cta = computed(() => (item.value ? ctaFor(item.value) : null))
// One button fits: the call to action when it works here -- in the upsell version, a preview --
// and otherwise the upgrade. The panel has room for both.
const upgradeCta = computed(() => (item.value ? upsellFor(item.value)?.primaryCta ?? null : null))
const moreCount = computed(() => bannerItems.value.length - 1)

// Once per item per page load, however often the banner re-renders.
const loggedShown = new Set<string>()
watch(item, current => {
  if (current && !loggedShown.has(current.id)) {
    loggedShown.add(current.id)
    logEvent('whats_new_shown', { item_id: current.id, more_count: moreCount.value })
  }
}, { immediate: true })

function dismiss () {
  const ids = bannerItems.value.map(each => each.id)
  logEvent('whats_new_dismissed', { item_id: item.value?.id, count: ids.length })
  void whatsNewStore.markSeen(ids)
}

function followCta () {
  // Capture both before markSeen: it drops the item from bannerItems synchronously, so `cta`
  // recomputes to null.
  const current = item.value
  const currentCta = cta.value
  if (!current || !currentCta) {
    return
  }
  void whatsNewStore.markSeen([current.id])
  followWhatsNewCta(router, current.id, currentCta, 'banner', action => emit('action', action))
}

function upgrade () {
  const current = item.value
  const action = upgradeCta.value?.action
  if (!current || !action) {
    return
  }
  void whatsNewStore.markSeen([current.id])
  followWhatsNewUpgrade(current.id, 'banner', action)
}

function openPanel () {
  whatsNewStore.openPanel('banner')
}
</script>
